/**
 * Formats a date string to German locale format with time
 *
 * @param dateString - ISO date string or any valid date string
 * @param options - Optional formatting configuration
 * @param options.includeTime - Whether to include time in the output (default: true)
 * @param options.locale - Locale for formatting (default: "de-DE")
 *
 * @returns Formatted date string in format "17.08.25 um 20:43" or "17.08.25" or "-" for invalid dates
 *
 * @example
 * ```typescript
 * formatDate("2025-08-17T20:43:00Z") // "17.08.25 um 20:43"
 * formatDate("2025-08-17T20:43:00Z", { includeTime: false }) // "17.08.25"
 * formatDate("") // "-"
 * formatDate(null) // "-"
 * ```
 */
export const formatDate = (
  dateString: string | null | undefined,
  options: {
    includeTime?: boolean;
    locale?: string;
  } = {}
): string => {
  const { includeTime = true } = options;

  // Handle empty, null, or undefined values
  if (!dateString || dateString.trim() === "") {
    return "-";
  }

  const date = new Date(dateString);

  // Check if the date is valid
  if (isNaN(date.getTime())) {
    return "-";
  }

  // Format: DD.MM.YY
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = String(date.getFullYear()).slice(-2);

  let formatted = `${day}.${month}.${year}`;

  if (includeTime) {
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    formatted += ` um ${hours}:${minutes}`;
  }

  return formatted;
};
